import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * @type {HTMLCanvasElement | null}
   */
  #samplingCanvas = null;

  /**
   * @type {CanvasRenderingContext2D | null}
   */
  #samplingContext = null;

  /**
   * @type {number | null}
   */
  #overlayRaf = null;

  /**
   * @type {HTMLElement | null}
   */
  #mobileOverlay = null;

  #mobileBreakpoint = window.matchMedia('(max-width: 749px)');

  #mobileOverlayObserver = new MutationObserver(() => {
    this.#cleanupDuplicateMobileOverlays();
    this.#queueMobileOverlayThemeUpdate();
  });

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  #getSamplingContext = () => {
    if (this.#samplingContext) return this.#samplingContext;
    this.#samplingCanvas = document.createElement('canvas');
    this.#samplingCanvas.width = 1;
    this.#samplingCanvas.height = 1;
    this.#samplingContext = this.#samplingCanvas.getContext('2d', { willReadFrequently: true });
    return this.#samplingContext;
  };

  #getElementBrightness = (element) => {
    if (!(element instanceof Element)) return null;

    if (element instanceof HTMLImageElement && element.complete && element.naturalWidth > 0) {
      try {
        const context = this.#getSamplingContext();
        if (!context) return null;

        const rect = element.getBoundingClientRect();
        const sampleX = Math.min(
          element.naturalWidth - 1,
          Math.max(0, Math.round(((window.innerWidth * 0.5) - rect.left) / rect.width * element.naturalWidth))
        );
        const sampleY = Math.min(
          element.naturalHeight - 1,
          Math.max(0, Math.round((20 - rect.top) / rect.height * element.naturalHeight))
        );

        context.clearRect(0, 0, 1, 1);
        context.drawImage(element, sampleX, sampleY, 1, 1, 0, 0, 1, 1);
        const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
        return (r * 299 + g * 587 + b * 114) / 1000;
      } catch {
        return null;
      }
    }

    if (element instanceof HTMLVideoElement && element.videoWidth > 0 && element.videoHeight > 0) {
      try {
        const context = this.#getSamplingContext();
        if (!context) return null;

        const rect = element.getBoundingClientRect();
        const sampleX = Math.min(
          element.videoWidth - 1,
          Math.max(0, Math.round(((window.innerWidth * 0.5) - rect.left) / rect.width * element.videoWidth))
        );
        const sampleY = Math.min(
          element.videoHeight - 1,
          Math.max(0, Math.round((20 - rect.top) / rect.height * element.videoHeight))
        );

        context.clearRect(0, 0, 1, 1);
        context.drawImage(element, sampleX, sampleY, 1, 1, 0, 0, 1, 1);
        const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
        return (r * 299 + g * 587 + b * 114) / 1000;
      } catch {
        return null;
      }
    }

    const style = window.getComputedStyle(element);
    const backgroundColor = style.backgroundColor;
    const match = backgroundColor.match(/rgba?\(([^)]+)\)/);
    if (match) {
      const parts = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
      if (parts.length >= 3) {
        const alpha = parts[3] ?? 1;
        if (alpha > 0.05) {
          const [r, g, b] = parts;
          return (r * 299 + g * 587 + b * 114) / 1000;
        }
      }
    }

    return null;
  };

  #updateMobileOverlayTheme = () => {
    this.#overlayRaf = null;

    const overlay = this.#mobileOverlay;
    if (!(overlay instanceof HTMLElement)) return;

    const shouldUseOverlay = this.#mobileBreakpoint.matches;
    if (shouldUseOverlay) {
      if (overlay.parentElement !== document.body) {
        document.body.appendChild(overlay);
      }

      overlay.dataset.mounted = 'body';
      overlay.hidden = false;
      this.#cleanupDuplicateMobileOverlays();
      return;
    }

    if (overlay.parentElement === document.body) {
      this.appendChild(overlay);
    }

    overlay.hidden = false;
    overlay.removeAttribute('data-mounted');
    this.#cleanupDuplicateMobileOverlays();
  };

  #cleanupDuplicateMobileOverlays = () => {
    const overlay = this.#mobileOverlay;
    if (!(overlay instanceof HTMLElement)) return;

    this.querySelectorAll('.header-mobile-overlay').forEach((candidate) => {
      if (candidate !== overlay) {
        candidate.remove();
      }
    });
  };

  #queueMobileOverlayThemeUpdate = () => {
    if (this.#overlayRaf != null) return;
    this.#overlayRaf = window.requestAnimationFrame(this.#updateMobileOverlayTheme);
  };

  #handleMobileOverlayClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const searchTrigger = target.closest('[data-overlay-search]');
    if (searchTrigger) {
      const realSearchButton = this.querySelector(
        ".header__row--top search-button .header-actions-ow__icon-button, .header__row--mobile-search .header-mobile-search__button"
      );
      if (realSearchButton instanceof HTMLElement) {
        realSearchButton.click();
        event.preventDefault();
      }
      return;
    }

    const cartTrigger = target.closest('[data-overlay-cart]');
    if (cartTrigger) {
      const realCartTrigger = this.querySelector(
        '.header__row--top header-actions cart-drawer-component > .header-actions__action, .header__row--top header-actions .action__cart'
      );
      const drawer = this.querySelector('.header__row--top header-drawer');
      const details = drawer?.querySelector('.menu-drawer-container');

      if (realCartTrigger instanceof HTMLElement) {
        const isMenuDrawerOpen =
          details instanceof HTMLDetailsElement && (details.classList.contains('menu-open') || details.hasAttribute('open'));

        if (isMenuDrawerOpen && typeof drawer?.close === 'function') {
          drawer.close();
          window.setTimeout(() => {
            realCartTrigger.click();
          }, 180);
        } else {
          realCartTrigger.click();
        }

        event.preventDefault();
      }
      return;
    }

    const menuTrigger = target.closest('[data-overlay-menu]');
    if (menuTrigger) {
      const drawer = this.querySelector('.header__row--top header-drawer');
      const details = drawer?.querySelector('.menu-drawer-container');
      const realMenuTrigger = drawer?.querySelector('.header__icon--summary');

      if (details instanceof HTMLDetailsElement && realMenuTrigger instanceof HTMLElement) {
        if (details.classList.contains('menu-open') && typeof drawer?.close === 'function') {
          drawer.close();
        } else {
          if (!details.hasAttribute('open')) {
            details.setAttribute('open', '');
          }

          if (typeof drawer?.open === 'function') {
            drawer.open({ target: details });
          } else {
            realMenuTrigger.click();
          }
        }

        event.preventDefault();
      }
    }
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        this.dataset.stickyState = isIntersecting && scrollTop <= 0 ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = scrollTop <= 0;
      const nextStickyState = isAtTop ? 'inactive' : 'active';

      if (this.dataset.stickyState !== nextStickyState) {
        this.dataset.stickyState = nextStickyState;
        changeMetaThemeColor(this.refs.headerRowTop);
      }

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#mobileOverlay = this.querySelector('.header-mobile-overlay');
    this.#resizeObserver.observe(this);
    this.#mobileOverlayObserver.observe(this, { childList: true });
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#mobileOverlay?.addEventListener('click', this.#handleMobileOverlayClick);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }

    this.#mobileBreakpoint.addEventListener('change', this.#queueMobileOverlayThemeUpdate);
    this.#queueMobileOverlayThemeUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#mobileOverlayObserver.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    this.#mobileBreakpoint.removeEventListener('change', this.#queueMobileOverlayThemeUpdate);

    if (this.#overlayRaf != null) {
      window.cancelAnimationFrame(this.#overlayRaf);
      this.#overlayRaf = null;
    }

    this.#mobileOverlay?.removeEventListener('click', this.#handleMobileOverlayClick);

    if (this.#mobileOverlay?.parentElement === document.body) {
      this.appendChild(this.#mobileOverlay);
      this.#mobileOverlay.removeAttribute('data-mounted');
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
